import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Building, Plus, Home, Eye, Edit, Trash2, LogOut, 
  Bed, Bath, MapPin, PoundSterling, Clock, CheckCircle, AlertCircle, X, Save
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LandlordDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isArabic } = useLanguage();
  const [landlordName, setLandlordName] = useState('');
  const [editingListing, setEditingListing] = useState(null);
  
  // Listings from API
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('landlordLoggedIn');
    const name = localStorage.getItem('landlordName');
    const landlordId = localStorage.getItem('landlordId');
    
    if (!isLoggedIn) {
      navigate('/landlord/login');
    } else {
      setLandlordName(name || 'Landlord');
      fetchListings(landlordId);
    }
  }, [navigate]);

  const fetchListings = async (landlordId) => {
    try {
      const response = await fetch(`${API_URL}/api/listings`);
      if (response.ok) {
        const data = await response.json();
        // Filter by landlord_id if available, otherwise show all
        const myListings = landlordId 
          ? data.filter(l => l.landlord_id === landlordId)
          : data;
        setListings(myListings);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('landlordListings');
      if (saved) setListings(JSON.parse(saved));
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('landlordLoggedIn');
    localStorage.removeItem('landlordName');
    localStorage.removeItem('landlordId');
    navigate('/landlord/login');
  };

  const handleDeleteListing = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await fetch(`${API_URL}/api/listings/${id}`, { method: 'DELETE' });
      toast({ title: "Listing deleted" });
      fetchListings(localStorage.getItem('landlordId'));
    } catch (error) {
      // Fallback to localStorage
      const updated = listings.filter(l => l.id !== id);
      setListings(updated);
      localStorage.setItem('landlordListings', JSON.stringify(updated));
    }
  };

  const handleEditListing = (listing) => {
    setEditingListing({...listing});
  };

  const handleSaveEdit = async () => {
    if (!editingListing) return;
    
    try {
      const response = await fetch(`${API_URL}/api/listings/${editingListing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingListing.title,
          price: parseFloat(editingListing.price) || 0,
          description: editingListing.description,
          address: editingListing.address,
          city: editingListing.city,
          postcode: editingListing.postcode,
          bedrooms: parseInt(editingListing.bedrooms) || 0,
          bathrooms: parseInt(editingListing.bathrooms) || 0
        })
      });
      
      if (response.ok) {
        toast({ title: "Listing updated!" });
        setEditingListing(null);
        fetchListings(localStorage.getItem('landlordId'));
      }
    } catch (error) {
      // Fallback to localStorage
      const updated = listings.map(l => l.id === editingListing.id ? editingListing : l);
      setListings(updated);
      localStorage.setItem('landlordListings', JSON.stringify(updated));
      setEditingListing(null);
      toast({ title: "Listing updated!" });
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
      case 'live':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> {isArabic ? 'نشط' : 'Live'}</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" /> {isArabic ? 'قيد المراجعة' : 'Pending'}</Badge>;
      case 'draft':
        return <Badge className="bg-gray-500"><Edit className="w-3 h-3 mr-1" /> {isArabic ? 'مسودة' : 'Draft'}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4`}>
          <div>
            <h1 className="text-3xl font-bold text-[#404040]">
              {isArabic ? `مرحباً، ${landlordName}` : `Welcome, ${landlordName}`}
            </h1>
            <p className="text-gray-600 mt-1">
              {isArabic ? 'أدر عقاراتك وقوائمك' : 'Manage your properties and listings'}
            </p>
          </div>
          <div className={`flex gap-3 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <Link to="/landlord/add-listing">
              <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white">
                <Plus className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                {isArabic ? 'إضافة عقار' : 'Add Listing'}
              </Button>
            </Link>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
              {isArabic ? 'تسجيل الخروج' : 'Logout'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className={`p-3 bg-[#FF8C42]/10 rounded-lg ${isArabic ? 'ml-4' : 'mr-4'}`}>
                  <Building className="w-8 h-8 text-[#FF8C42]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#404040]">{listings.length}</p>
                  <p className="text-gray-600 text-sm">{isArabic ? 'إجمالي العقارات' : 'Total Listings'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className={`p-3 bg-green-100 rounded-lg ${isArabic ? 'ml-4' : 'mr-4'}`}>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#404040]">
                    {listings.filter(l => l.status === 'live').length}
                  </p>
                  <p className="text-gray-600 text-sm">{isArabic ? 'عقارات نشطة' : 'Live Listings'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className={`p-3 bg-yellow-100 rounded-lg ${isArabic ? 'ml-4' : 'mr-4'}`}>
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#404040]">
                    {listings.filter(l => l.status === 'pending').length}
                  </p>
                  <p className="text-gray-600 text-sm">{isArabic ? 'قيد المراجعة' : 'Pending Review'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className={`p-3 bg-blue-100 rounded-lg ${isArabic ? 'ml-4' : 'mr-4'}`}>
                  <Eye className="w-8 h-8 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#404040]">
                    {listings.reduce((acc, l) => acc + (l.views || 0), 0)}
                  </p>
                  <p className="text-gray-600 text-sm">{isArabic ? 'إجمالي المشاهدات' : 'Total Views'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Listings */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="text-[#404040]">
              {isArabic ? 'عقاراتي' : 'My Listings'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {listings.length === 0 ? (
              <div className="text-center py-12">
                <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  {isArabic ? 'لا توجد عقارات بعد' : 'No listings yet'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {isArabic 
                    ? 'ابدأ بإضافة عقارك الأول للإيجار أو البيع'
                    : 'Start by adding your first property for rent or sale'
                  }
                </p>
                <Link to="/landlord/add-listing">
                  <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white">
                    <Plus className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                    {isArabic ? 'إضافة عقار' : 'Add Your First Listing'}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {listings.map((listing) => (
                  <div 
                    key={listing.id} 
                    className="flex flex-col md:flex-row gap-4 p-4 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="w-full md:w-48 h-32 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {listing.image ? (
                        <img src={listing.image} alt={listing.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Home className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className={`flex items-start justify-between gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <div>
                          <h3 className="text-lg font-semibold text-[#404040]">{listing.title}</h3>
                          <div className={`flex items-center text-gray-600 text-sm mt-1 ${isArabic ? 'flex-row-reverse' : ''}`}>
                            <MapPin className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                            {listing.address}
                          </div>
                        </div>
                        {getStatusBadge(listing.status)}
                      </div>
                      
                      <div className={`flex items-center gap-4 mt-3 text-sm text-gray-600 ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <span className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                          <Bed className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                          {listing.bedrooms} {isArabic ? 'غرف' : 'beds'}
                        </span>
                        <span className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                          <Bath className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                          {listing.bathrooms} {isArabic ? 'حمامات' : 'baths'}
                        </span>
                        <span className={`flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                          <PoundSterling className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                          £{listing.price?.toLocaleString()}{listing.listingType === 'rent' ? '/mo' : ''}
                        </span>
                      </div>

                      <div className={`flex gap-2 mt-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                        <Link to={`/property/${listing.id}`}>
                          <Button size="sm" variant="outline">
                            <Eye className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                            {isArabic ? 'عرض' : 'View'}
                          </Button>
                        </Link>
                        <Button size="sm" variant="outline" onClick={() => handleEditListing(listing)}>
                          <Edit className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                          {isArabic ? 'تعديل' : 'Edit'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteListing(listing.id)}
                        >
                          <Trash2 className={`w-4 h-4 ${isArabic ? 'ml-1' : 'mr-1'}`} />
                          {isArabic ? 'حذف' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Listing Modal */}
      {editingListing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">{isArabic ? 'تعديل العقار' : 'Edit Listing'}</h3>
                <Button variant="ghost" size="sm" onClick={() => setEditingListing(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>{isArabic ? 'العنوان' : 'Title'}</Label>
                  <Input
                    value={editingListing.title || ''}
                    onChange={(e) => setEditingListing({...editingListing, title: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isArabic ? 'السعر' : 'Price (£)'}</Label>
                    <Input
                      type="number"
                      value={editingListing.price || ''}
                      onChange={(e) => setEditingListing({...editingListing, price: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{isArabic ? 'المدينة' : 'City'}</Label>
                    <Input
                      value={editingListing.city || ''}
                      onChange={(e) => setEditingListing({...editingListing, city: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>{isArabic ? 'العنوان' : 'Address'}</Label>
                  <Input
                    value={editingListing.address || ''}
                    onChange={(e) => setEditingListing({...editingListing, address: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{isArabic ? 'غرف النوم' : 'Bedrooms'}</Label>
                    <Input
                      type="number"
                      value={editingListing.bedrooms || ''}
                      onChange={(e) => setEditingListing({...editingListing, bedrooms: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{isArabic ? 'الحمامات' : 'Bathrooms'}</Label>
                    <Input
                      type="number"
                      value={editingListing.bathrooms || ''}
                      onChange={(e) => setEditingListing({...editingListing, bathrooms: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{isArabic ? 'الرمز البريدي' : 'Postcode'}</Label>
                    <Input
                      value={editingListing.postcode || ''}
                      onChange={(e) => setEditingListing({...editingListing, postcode: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>{isArabic ? 'الوصف' : 'Description'}</Label>
                  <Textarea
                    value={editingListing.description || ''}
                    onChange={(e) => setEditingListing({...editingListing, description: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={handleSaveEdit} className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">
                    <Save className="w-4 h-4 mr-2" /> {isArabic ? 'حفظ التغييرات' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditingListing(null)}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
